import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { saasCheckout } from '../../api';

const RENEW_WINDOW_DAYS = 7;

export const RenewalBanner = ({ lang, sub }) => {
  const [busy, setBusy] = useState(false);
  const ar = lang === 'ar';
  if (!sub) return null;

  const active = sub.status === 'active' && sub.provider !== 'stripe' && !sub.manual_grant && sub.renewal_at;
  const daysLeft = active ? Math.ceil((new Date(sub.renewal_at) - new Date()) / 86400000) : null;
  const expiring = active && daysLeft !== null && daysLeft <= RENEW_WINDOW_DAYS;
  const expired = sub.status === 'none' && !!sub.previous_plan;
  if (!expiring && !expired) return null;

  const plan = expired ? sub.previous_plan : sub.plan;
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const dateStr = new Date(expired ? sub.expired_at : sub.renewal_at).toLocaleDateString(ar ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const renew = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await saasCheckout(plan, lang);
      window.location.href = checkout_url;
    } catch (e) {
      toast({ title: e?.response?.data?.detail || (ar ? 'تعذّر بدء التجديد' : 'Could not start renewal'), variant: 'destructive' });
      setBusy(false);
    }
  };

  const tone = expired ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100' : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100';
  const icon = expired ? 'text-rose-600' : 'text-amber-600';
  const msg = expired
    ? (ar ? `انتهى اشتراكك في باقة ${planLabel} بتاريخ ${dateStr}. جدّد الآن لاستعادة الوصول إلى بياناتك وتقاريرك.` : `Your ${planLabel} subscription expired on ${dateStr}. Renew now to regain access to your books and reports.`)
    : daysLeft <= 0
      ? (ar ? `ينتهي اشتراكك في باقة ${planLabel} اليوم.` : `Your ${planLabel} subscription ends today.`)
      : (ar ? `ينتهي اشتراكك في باقة ${planLabel} خلال ${daysLeft} ${daysLeft === 1 ? 'يوم' : 'أيام'} (${dateStr}). جدّد الآن لتجنّب انقطاع الخدمة.` : `Your ${planLabel} subscription ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${dateStr}). Renew now to avoid any interruption.`);

  return (
    <div className={`mb-6 rounded-2xl border p-4 flex flex-wrap items-center gap-3 ${tone}`} data-testid={expired ? 'renewal-banner-expired' : 'renewal-banner-expiring'}>
      <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${icon}`} />
      <div className="flex-1 text-sm min-w-[220px]" data-testid="renewal-banner-text">{msg}</div>
      <Button onClick={renew} disabled={busy} className="gold-button rounded-md h-9 px-4 text-xs" data-testid="renew-now-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 me-1" /> {ar ? `تجديد الآن — ${planLabel}` : `Renew now — ${planLabel}`}</>}
      </Button>
    </div>
  );
};
